export type BrowserConnectionStatus = 'idle' | 'reconnecting' | 'offline';

/** A reconnect renews the viewer ticket, never the browser or its control owner. */
export function browserReconnect(reconnect: (signal: AbortSignal) => Promise<void>, status: (value: BrowserConnectionStatus) => void) {
  let disposed = false, pending = false, attempts = 0;
  let timer: ReturnType<typeof setTimeout> | undefined, stable: ReturnType<typeof setTimeout> | undefined;
  let request: AbortController | undefined;
  const available = () => navigator.onLine !== false && document.visibilityState !== 'hidden';
  const clear = () => { clearTimeout(timer); timer = undefined; };
  const schedule = (immediate = false) => {
    if (disposed || !pending || request || timer) return;
    status(navigator.onLine === false ? 'offline' : 'reconnecting');
    if (!available()) return;
    timer = setTimeout(() => { timer = undefined; void run(); }, immediate ? 0 : Math.min(15_000, 1000 * 2 ** Math.min(attempts, 4)));
  };
  const run = async () => {
    if (disposed || !pending || !available()) { schedule(); return; }
    const controller = new AbortController(); request = controller; attempts++;
    // The new viewer reports connect/disconnect; a successful HTTP ticket is not a live connection.
    pending = false;
    try { await reconnect(controller.signal); }
    catch (error) {
      if (disposed || controller.signal.aborted) return;
      if (error instanceof Error && ['browser_expired', 'browser_control_changed', 'client_required', 'origin_rejected'].includes(error.message)) {
        status('idle'); return;
      }
      pending = true;
    } finally { if (request === controller) request = undefined; if (!disposed) schedule(); }
  };
  const wake = () => {
    if (!pending) return;
    clear(); schedule(true);
  };
  window.addEventListener('online', wake); window.addEventListener('offline', wake);
  document.addEventListener('visibilitychange', wake);
  return {
    disconnected() { if (disposed) return; clearTimeout(stable); pending = true; schedule(); },
    connected() {
      if (disposed) return;
      pending = false; clear(); status('idle'); clearTimeout(stable);
      stable = setTimeout(() => { attempts = 0; }, 30_000);
    },
    dispose() {
      disposed = true; clear(); clearTimeout(stable); request?.abort();
      window.removeEventListener('online', wake); window.removeEventListener('offline', wake);
      document.removeEventListener('visibilitychange', wake);
    },
  };
}
